import React, { Component } from 'react';
import { SubHeading } from '../common';
import { formatEpochToDateString } from '../../utils/commonUtils';

interface LatestNAV {
  dayEndDate: string;
  dayEndNav: number;
}

interface Returns {
  return1W: number;
  return1M: number;
  return3M: number;
  return6M: number;
  return1Y: number;
  return3Y: number;
}

interface FundManager {
  name: string;
}

interface ExitLoadDetail {
  percentage: number;
  days: number;
}

interface NAVHistoryProps {
  name: string;
  latestNav: LatestNAV;
  returns: Returns;
  category: string;
  subCategory: string;
  fundManagers: FundManager[];
  minAmount: number;
  minSipAmount: number;
  exitLoadDetail: ExitLoadDetail;
  inceptionDate: string;
  fundType: string;
}

interface TableData {
  headers: React.ReactNode[];
  body: React.ReactNode[][];
}

class NAVHistory extends Component<NAVHistoryProps> {
  renderTable = (data: TableData, isScrollable: boolean = false) => {
    const table = (
      <table
        className={`lil-border-separate lil-w-full lil-shadow-sm lil-border-sm lil-rounded-xl lil-border-spacing-0 lil-border-grey-300 lil-mt-4 ${
          isScrollable ? 'sm:lil-mx-6' : ''
        }`}
        style={{
          tableLayout: 'fixed',
          width: '100%',
          ...(isScrollable
            ? {
                minWidth: '700px',
              }
            : {}),
        }}
      >
        <thead>
          <tr style={{ backgroundColor: '#D9F2F4' }}>
            {data.headers.map((header, index) => {
              let headerClasses =
                'lil-text-center lil-py-4 lil-pl-4 sm:lil-pl-2 lil-border-b-sm lil-border-grey-300';
              if (index === 0) {
                headerClasses += ' lil-border-r-sm lil-rounded-tl-xl';
              } else if (index === data.headers.length - 1) {
                headerClasses += ' lil-rounded-tr-xl';
              } else {
                headerClasses += ' lil-border-r-sm';
              }
              return (
                <th key={index} className={headerClasses}>
                  <p
                    className="lil-text-s-14 lil-font-semibold"
                    style={{ color: '#437276' }}
                  >
                    {header}
                  </p>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.body.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => {
                let cellClasses =
                  'lil-text-center lil-pl-4 lil-font-medium lil-text-left lil-border-grey-300 lil-border-b-sm lil-py-4';
                if (cellIndex !== row.length - 1) {
                  cellClasses += ' lil-border-r-sm lil-pr-2';
                }
                if (rowIndex === data.body.length - 1) {
                  cellClasses = cellClasses.replace('lil-border-b-sm', '');
                }
                return (
                  <td key={cellIndex} className={cellClasses}>
                    <span className="lil-text-base">{cell}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );

    if (isScrollable) {
      return (
        <div
          className="lil-overflow-x-auto lil-scrolling-touch sm:lil--mx-6"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {table}
        </div>
      );
    }

    return table;
  };

  formatAndTruncateName = (
    fullName: string,
    maxLength: number = 25
  ): string => {
    const namePart = fullName.split('Fund')[0];
    let processedName = '';
    if (namePart) {
      processedName = namePart.trim();
    }
    if (processedName.length > maxLength) {
      return processedName.slice(0, maxLength) + '...';
    }
    return processedName;
  };

  render() {
    const {
      name,
      latestNav,
      category,
      subCategory,
      fundManagers,
      minAmount,
      minSipAmount,
      exitLoadDetail,
      returns,
      inceptionDate,
      fundType,
    } = this.props;

    const DetailsTableData: TableData = {
      headers: ['FUND', name || 'N/A'],
      body: [
        [
          `NAV ${
            latestNav
              ? `(${formatEpochToDateString(latestNav.dayEndDate)})`
              : ''
          }`,
          latestNav ? `₹${latestNav.dayEndNav.toFixed(2)}` : 'N/A',
        ],
        ['Class', `${category || ''} ${subCategory ? `| ${subCategory}` : ''}`],
        ...(fundType && fundType.length > 0
          ? [['Type', fundType.replace(' Investment Company', '')]]
          : []),
        ...(inceptionDate
          ? [['Inception Date', formatEpochToDateString(inceptionDate)]]
          : []),
        [
          'Fund Manager',
          fundManagers && fundManagers.length > 0
            ? fundManagers.map((manager) => manager.name).join(', ')
            : 'N/A',
        ],
      ],
    };

    const FinancialsTableData: TableData = {
      headers: ['FUND', name || 'N/A'],
      body: [
        [`Initial Price`, '₹10'],
        [
          'Min. Investment Amount',
          `SIP ₹${minSipAmount || 'N/A'} and Lump. ₹${minAmount || 'N/A'}`,
        ],
        [
          'Exit Load',
          exitLoadDetail
            ? `${exitLoadDetail.percentage}% within ${exitLoadDetail.days} days`
            : 'N/A',
        ],
      ],
    };

    const ReturnsTableData: TableData = {
      headers: ['Returns', '1W', '1M', '3M', '6M', '1Y', '3Y'],
      body: [
        [
          this.formatAndTruncateName(name || 'N/A', 20),
          returns && returns.return1W !== undefined
            ? `${returns.return1W}%`
            : 'N/A',
          returns && returns.return1M !== undefined
            ? `${returns.return1M}%`
            : 'N/A',
          returns && returns.return3M !== undefined
            ? `${returns.return3M}%`
            : 'N/A',
          returns && returns.return6M !== undefined
            ? `${returns.return6M}%`
            : 'N/A',
          returns && returns.return1Y !== undefined
            ? `${returns.return1Y}%`
            : 'N/A',
          returns && returns.return3Y !== undefined
            ? `${returns.return3Y}%`
            : 'N/A',
        ],
      ],
    };

    return (
      <div className="lil-mt-6">
        <SubHeading title={`NAV History - ${name}`} />
        <div
          itemScope
          itemType="http://schema.org/Table"
          className="lil-pt-0 sm:lil-w-full sm:lil-pb-6"
        >
          {this.renderTable(DetailsTableData)}
          {this.renderTable(FinancialsTableData)}
          {this.renderTable(ReturnsTableData, true)}
        </div>
      </div>
    );
  }
}

export default NAVHistory;



// WEBPACK FOOTER //
// ./components/singleFundNewCmp/navHistory.tsx